import React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from 'web/src/auth/useAuth'
import { getMoteLogin, identifyMaybeAndBroadcast } from 'web/src/shared/Auth'
import { getGodModeOptions, log } from 'web/src/shared/Utils'
import { useAccountQuery, useTrackPurchaseFlow } from '@mote/api'
import { TickOutlineIcon } from '@mote/assets/icons'
import { useQueryParams } from '@mote/helpers'
import { useGoalsState } from '@mote/helpers/hooks/business'
import { OnboardingLayout } from '../../layouts'
import { MessageCard } from '../Onboarding/components'

const ERROR_TYPES = {
  UNABLE_TO_SYNC: 'UNABLE_TO_SYNC',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
}

const MAX_POLLS = 20
const POLL_SLEEP = 1000
const NON_IDEAL_STATE_SVG = '/images/illustrations/non-ideal-state.svg'

export const CheckoutCallbackScreen = () => {
  const [state, setState] = React.useState({
    loading: true,
    success: false,
    error: null
  })
  const { user, updateUser } = useAuth()
  const history = useHistory()
  const { queryParams } = useQueryParams()
  const { mutate: queryAccount } = useAccountQuery()
  const { onboardingComplete } = useGoalsState()
  const { mutate: trackPurchaseFlow } = useTrackPurchaseFlow()

  const messageCardProps = React.useMemo(() => {
    if (state.loading) {
      return {
        title: 'Hold on',
        message:
          'Waiting to sync your account status with the payment gateway...',
        loading: true,
        showTextOnLoading: true
      }
    }

    if (state.success) {
      return {
        title: 'Your purchase was successful',
        message: `Thanks for purchasing Mote Unlimited. You’re officially Awesome! Hit the button below to ${
          onboardingComplete
            ? 'continue to your homepage'
            : 'set up your account and get started'
        }.`,
        icon: (
          <TickOutlineIcon width={60} height={60} style={{ margin: '-12px' }} />
        ),
        ctaProps: {
          text: onboardingComplete
            ? 'Continue to dashboard'
            : 'Set up my account',
          onClick: handleProceed
        }
      }
    }

    if (state.error && state.error === ERROR_TYPES.UNABLE_TO_SYNC) {
      return {
        title: 'Oh no!',
        message: (
          <>
            We were not able to sync your payment information. Refresh the page
            and contact{' '}
            <Link href='mailto:support@mote.com'>support@mote.com</Link> if your
            payment is not yet reflected.
          </>
        ),
        icon: <img src={NON_IDEAL_STATE_SVG} alt='Error occurred' />
      }
    }

    return {
      title: 'Oh no!',
      message: (
        <>
          An unknown error occurred. If the problem persists kindly contact{' '}
          <Link href='mailto:support@mote.com'>support@mote.com</Link>
        </>
      ),
      icon: <img src={NON_IDEAL_STATE_SVG} alt='Error occurred' />
    }
  }, [state, onboardingComplete])

  React.useEffect(() => {
    if (!queryParams.state || !user) {
      return history.push('/')
    }

    pollAccountStatus()
  }, [])

  function handleProceed() {
    history.push(onboardingComplete ? '/' : '/onboarding')
  }

  async function pollAccountStatus(chargeBeePolls = 0) {
    const response = await queryAccount({
      variables: getGodModeOptions() || {}
    })

    if (!response || response.error || !response.data) {
      setState(prev => ({
        ...prev,
        error: ERROR_TYPES.UNKNOWN_ERROR,
        loading: false
      }))
      return
    }

    const { account } = response.data

    if (
      !(
        account.isPaid ||
        (account.isInstitutionalAdmin &&
          account.currentLicense &&
          account.currentLicense.totalPaidSeats)
      )
    ) {
      if (chargeBeePolls < MAX_POLLS) {
        trackPurchaseFlow({
          productEnum: 'unlimited',
          step: 6,
          stage: 'Returned to account page from CB, syncing ...'
        })
        log('Going to poll again')
        setTimeout(() => pollAccountStatus(chargeBeePolls + 1), POLL_SLEEP)
      } else {
        setState(prev => ({
          ...prev,
          error: ERROR_TYPES.UNABLE_TO_SYNC,
          loading: false
        }))

        trackPurchaseFlow({
          productEnum: 'unlimited',
          step: 7,
          stage: 'Failed to CB sync on account page',
          hasFailed: true
        })
      }
    } else {
      identifyMaybeAndBroadcast()
      trackPurchaseFlow({
        productEnum: 'unlimited',
        step: 7,
        stage: 'CB synced on account page',
        hasCompleted: true
      })
      updateUser(getMoteLogin())
      setState(prev => ({
        ...prev,
        loading: false,
        success: true
      }))
    }
  }

  return (
    <OnboardingLayout skipGreeting>
      <MessageCard {...messageCardProps} />
    </OnboardingLayout>
  )
}

const Link = styled.a`
  color: #ac0ae8;
  font-size: 15px;
  font-weight: 500;

  &:hover,
  &:focus,
  &:visited {
    color: #ac0ae8;
  }

  &:hover {
    text-decoration: none;
  }
`
